import React, { useEffect, useState, Suspense, lazy } from 'react';
import { LanguageProvider } from './contexts/LanguageContext';
import { Navbar } from './components/Navbar';
import { Hero } from './components/Hero';
import { Mission, MissionPanels } from './components/Mission';
import { ConsularBanner } from './components/ConsularBanner';
import { LandingPage } from './components/LandingPage';
import { GoogleAnalytics } from './components/GoogleAnalytics';
import { useLanguage } from './contexts/LanguageContext';
// Lazy-loaded below-the-fold sections
const CommunityStrength = lazy(() => import('./components/CommunityStrength').then(m => ({
  default: m.CommunityStrength
})));
const Leadership = lazy(() => import('./components/Leadership').then(m => ({
  default: m.Leadership
})));
const AdvisoryCouncil = lazy(() => import('./components/AdvisoryCouncil').then(m => ({
  default: m.AdvisoryCouncil
})));
const MakeDifferenceCTA = lazy(() => import('./components/MakeDifferenceCTA').then(m => ({
  default: m.MakeDifferenceCTA
})));
const NewsBlog = lazy(() => import('./components/NewsBlog').then(m => ({
  default: m.NewsBlog
})));
const MediaScroller = lazy(() => import('./components/MediaScroller').then(m => ({
  default: m.MediaScroller
})));
const ConsularAffairs = lazy(() => import('./components/ConsularAffairs').then(m => ({
  default: m.ConsularAffairs
})));
const BusinessInvestments = lazy(() => import('./components/BusinessInvestments').then(m => ({
  default: m.BusinessInvestments
})));
const OurPeopleStories = lazy(() => import('./components/OurPeopleStories').then(m => ({
  default: m.OurPeopleStories
})));
const NonprofitDirectory = lazy(() => import('./components/NonprofitDirectory').then(m => ({
  default: m.NonprofitDirectory
})));
const ExploreHonduras = lazy(() => import('./components/ExploreHonduras').then(m => ({
  default: m.ExploreHonduras
})));
const HondurasQuiz = lazy(() => import('./components/HondurasQuiz').then(m => ({
  default: m.HondurasQuiz
})));
const FAQ = lazy(() => import('./components/FAQ').then(m => ({
  default: m.FAQ
})));
const Footer = lazy(() => import('./components/Footer').then(m => ({
  default: m.Footer
})));
const SocialMediaVoices = lazy(() => import('./components/SocialMediaVoices').then(m => ({
  default: m.SocialMediaVoices
})));
const ChatbaseWidget = lazy(() => import('./components/ChatbaseWidget').then(m => ({
  default: m.ChatbaseWidget
})));
// Lazy-loaded sub-pages
const AdminDashboard = lazy(() => import('./components/AdminDashboard').then(m => ({
  default: m.AdminDashboard
})));
const ConsularPage = lazy(() => import('./pages/ConsularPage').then(m => ({
  default: m.ConsularPage
})));
const BusinessMapPage = lazy(() => import('./pages/BusinessMapPage').then(m => ({
  default: m.BusinessMapPage
})));
const MemberProfilePage = lazy(() => import('./pages/MemberProfilePage').then(m => ({
  default: m.MemberProfilePage
})));
const NewsStoryPage = lazy(() => import('./pages/NewsStoryPage').then(m => ({
  default: m.NewsStoryPage
})));
const MembersDirectoryPage = lazy(() => import('./pages/MembersDirectoryPage').then(m => ({
  default: m.MembersDirectoryPage
})));
const SaveTheDatePage = lazy(() => import('./pages/SaveTheDatePage').then(m => ({
  default: m.SaveTheDatePage
})));
// Lightweight loading placeholder — subtle pulse instead of spinner to reduce visual jank
function SectionLoader() {
  return <div className="w-full py-16 flex items-center justify-center" data-id="element-0" data-mp="1i2j7fszipfjg">
      <div className="flex items-center gap-2" data-id="element-1" data-mp="1m4wcef6fx9f6">
        <div className="w-2 h-2 rounded-full bg-haa-blue/30 animate-pulse" data-id="element-2" data-mp="1m3l90ggwq3ol" />
        <div className="w-2 h-2 rounded-full bg-haa-blue/20 animate-pulse" style={{
        animationDelay: '150ms'
      }} data-id="element-3" data-mp="6ncmyfzirqz0" />
        <div className="w-2 h-2 rounded-full bg-haa-blue/10 animate-pulse" style={{
        animationDelay: '300ms'
      }} data-id="element-4" data-mp="qx5gpzii0qb0" />
      </div>
    </div>;
}
function AppContent({
  showLanding,
  setShowLanding
}: {
  showLanding: boolean;
  setShowLanding: (v: boolean) => void;
}) {
  const {
    setLanguage
  } = useLanguage();
  const [pendingScroll, setPendingScroll] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const hash = window.location.hash;
    return isSectionAnchor(hash) ? resolveAnchor(hash.slice(1)) : null;
  });
  const handleSelectLanguage = (lang: 'EN' | 'ES') => {
    setLanguage(lang);
    setShowLanding(false);
  };
  const handleConsularAccess = (lang: 'EN' | 'ES') => {
    setLanguage(lang);
    setShowLanding(false);
    setPendingScroll('consular-affairs');
  };
  // React to in-page hash changes (e.g. opening a /#leadership link while already on the site)
  useEffect(() => {
    const handler = () => {
      const hash = window.location.hash;
      if (isSectionAnchor(hash)) {
        setShowLanding(false);
        setPendingScroll(resolveAnchor(hash.slice(1)));
      }
    };
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, [setShowLanding]);
  useEffect(() => {
    if (!showLanding && pendingScroll) {
      // Lazy-loaded sections may not be mounted yet — retry briefly until the element exists.
      const start = Date.now();
      let cancelled = false;
      const tryScroll = () => {
        if (cancelled) return;
        const el = document.getElementById(pendingScroll);
        if (el) {
          el.scrollIntoView({
            behavior: 'smooth'
          });
          setPendingScroll(null);
          return;
        }
        if (Date.now() - start < 4000) {
          setTimeout(tryScroll, 150);
        } else {
          setPendingScroll(null);
        }
      };
      const timer = setTimeout(tryScroll, 100);
      return () => {
        cancelled = true;
        clearTimeout(timer);
      };
    }
  }, [showLanding, pendingScroll]);
  if (showLanding) {
    return <LandingPage onSelectLanguage={handleSelectLanguage} onConsularAccess={handleConsularAccess} data-id="element-5" data-mp="1wu23roz9z8ww" />;
  }
  return <div className="font-nunito w-full min-h-screen overflow-x-hidden" data-id="element-6" data-mp="25t5qd2xni6g5">
      <Navbar data-id="element-7" data-mp="25runr6fxz4qm" />
      <main data-id="element-8" data-mp="25vifc01tth2p">
        <Hero data-id="element-9" data-mp="2kq6ih7jr83f5" />
        <ConsularBanner data-id="element-10" data-mp="2kpwtnnag42b1" />
        <Mission data-id="element-11" data-mp="2kp552wf9n4u9" />

        {/* Each section loads independently for faster perceived performance */}
        <Suspense fallback={<SectionLoader data-id="element-13" data-mp="2fr3elqfig38r" />} data-id="element-12" data-mp="2fqk1qo4pw51l">
          <OurPeopleStories data-id="element-14" data-mp="2tiqhwa7vec11" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-16" data-mp="2thymaze9wear" />} data-id="element-15" data-mp="2thglrqkkvdx1">
          <MakeDifferenceCTA data-id="element-17" data-mp="2ph4ye60tyzhv" />
        </Suspense>
        <div className="max-w-5xl mx-auto px-4" data-id="element-18" data-mp="2pig2k6y6u99i">
          <div className="h-px bg-gradient-to-r from-transparent via-haa-blue/20 to-transparent" data-id="element-19" data-mp="33gxqa4o3flrf" />
        </div>
        <Suspense fallback={<SectionLoader data-id="element-21" data-mp="2yiw59e73xcdb" />} data-id="element-20" data-mp="2yicymtmwqael">
          <NewsBlog data-id="element-22" data-mp="2yjo18q4m9c44" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-24" data-mp="3i3d4uxc5qhyz" />} data-id="element-23" data-mp="3i3wd1mc09rzt">
          <MediaScroller data-id="element-25" data-mp="3i464lecp877m" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-27" data-mp="3n397as57i1cf" />} data-id="element-26" data-mp="3n2pufpuey359">
          <CommunityStrength data-id="element-28" data-mp="3n1ekt9eae168" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-30" data-mp="385ysp9x9ofch" />} data-id="element-29" data-mp="385gtq5j7zn0v">
          <MissionPanels data-id="element-31" data-mp="384noj8zwt5ku" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-33" data-mp="3d40i0cladawe" />} data-id="element-32" data-mp="3d4jomx5hkcv4">
          <Leadership data-id="element-34" data-mp="3d38m10ns1b5l" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-36" data-mp="2zdevmdwmiitr" />} data-id="element-35" data-mp="3d6cw2enxb146">
          <AdvisoryCouncil data-id="element-37" data-mp="2zc3s8f73bd36" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-39" data-mp="343lmkkelyff6" />} data-id="element-38" data-mp="2zbajx9shgfix">
          <MakeDifferenceCTA data-id="element-40" data-mp="344wxr5adupgb" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-42" data-mp="2pf7kibp0muzo" />} data-id="element-41" data-mp="345on3yddzqy5">
          <BusinessInvestments data-id="element-43" data-mp="2pg0hwm234855" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-45" data-mp="2udsis5kpj4lh" />} data-id="element-44" data-mp="2pd4mp5hmfxke">
          <ConsularAffairs data-id="element-46" data-mp="2uf3le22f26b0" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-48" data-mp="2gej79j7g4ynu" />} data-id="element-47" data-mp="2ubpjeuxnzz43">
          <SocialMediaVoices data-id="element-49" data-mp="2gfc4ntkimbtb" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-51" data-mp="2kfmhnq6q62ho" />} data-id="element-50" data-mp="2ggn9zxtlzrmj">
          <NonprofitDirectory data-id="element-52" data-mp="2ketd8vv8np2p" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-54" data-mp="30v3w0rhyb21j" />} data-id="element-53" data-mp="2khpbymewuhs7">
          <HondurasQuiz data-id="element-55" data-mp="30uc4psvdzqh2" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-57" data-mp="2vwk6kh45f84f" />} data-id="element-56" data-mp="30t139zpeyut4">
          <ExploreHonduras data-id="element-58" data-mp="2vxc3bv9hfbwa" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-60" data-mp="2qwxr010ulfsg" />} data-id="element-59" data-mp="2vug483lweh69">
          <FAQ data-id="element-61" data-mp="2qw689qscncjl" />
        </Suspense>
      </main>
      <Suspense fallback={null} data-id="element-62" data-mp="2qz0rxg3jgtby">
        <Footer data-id="element-63" data-mp="2lymjvy1xtjdx" />
      </Suspense>
      <Suspense fallback={null} data-id="element-64" data-mp="2lxl63ltjeisi">
        <ChatbaseWidget data-id="element-65" data-mp="2m0h2h5pqkzeu" />
      </Suspense>
    </div>;
}
// Short, shareable aliases that resolve to the actual section IDs in the DOM.
// Example: /#council scrolls to the <section id="advisory-council">.
const SECTION_ALIASES: Record<string, string> = {
  council: 'advisory-council',
  members: 'our-stories',
  consular: 'consular-affairs',
  business: 'business-investments',
  nonprofit: 'nonprofit-directory',
  explore: 'explore-honduras',
  trivia: 'honduras-trivia',
  socials: 'social-voices'
};
// Section anchors that, when present in the URL hash, should bypass the landing page
// and scroll directly to that section on the main site. Both canonical IDs and the
// friendly aliases above are accepted.
const SECTION_ANCHORS = new Set(['home', 'mission', 'community', 'leadership', 'advisory-council', 'our-stories', 'news', 'social-voices', 'explore-honduras', 'honduras-trivia', 'nonprofit-directory', 'business-investments', 'consular-affairs', 'faq', 'contact', ...Object.keys(SECTION_ALIASES)]);
function resolveAnchor(id: string): string {
  return SECTION_ALIASES[id] ?? id;
}
function isSectionAnchor(hash: string): boolean {
  if (!hash || !hash.startsWith('#')) return false;
  if (hash.startsWith('#/')) return false;
  const id = hash.slice(1);
  return SECTION_ANCHORS.has(id);
}
export function App() {
  const initialHash = typeof window !== 'undefined' ? window.location.hash : '';
  const [showLanding, setShowLanding] = useState(!isSectionAnchor(initialHash));
  const [showAdmin, setShowAdmin] = useState(() => window.location.hash === '#/admin');
  const [showConsular, setShowConsular] = useState(() => window.location.hash === '#/consular');
  const [showBusinessMap, setShowBusinessMap] = useState(() => window.location.hash.startsWith('#/business-map'));
  const [businessMapLang, setBusinessMapLang] = useState<'EN' | 'ES'>(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#/business-map')) return 'EN';
    const match = hash.match(/[?&]lang=(EN|ES)/);
    return match ? match[1] as 'EN' | 'ES' : 'EN';
  });
  const [showMembers, setShowMembers] = useState(() => window.location.hash === '#/members');
  const [showSaveTheDate, setShowSaveTheDate] = useState(() => window.location.hash === '#/save-the-date');
  const [showMember, setShowMember] = useState(() => window.location.hash.startsWith('#/member/'));
  const [memberSlug, setMemberSlug] = useState(() => {
    const hash = window.location.hash;
    return hash.startsWith('#/member/') ? hash.replace('#/member/', '') : '';
  });
  const [showNews, setShowNews] = useState(() => window.location.hash.startsWith('#/news/'));
  const [newsPostId, setNewsPostId] = useState(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#/news/')) return '';
    const raw = hash.replace('#/news/', '');
    return raw.split('?')[0];
  });
  const [newsLang, setNewsLang] = useState<'EN' | 'ES'>(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#/news/')) return 'EN';
    const match = hash.match(/[?&]lang=(EN|ES)/);
    return match ? match[1] as 'EN' | 'ES' : 'EN';
  });
  useEffect(() => {
    const handleHash = () => {
      const hash = window.location.hash;
      setShowAdmin(hash === '#/admin');
      setShowConsular(hash === '#/consular');
      const isBusinessMap = hash.startsWith('#/business-map');
      setShowBusinessMap(isBusinessMap);
      if (isBusinessMap) {
        const bmLangMatch = hash.match(/[?&]lang=(EN|ES)/);
        setBusinessMapLang(bmLangMatch ? bmLangMatch[1] as 'EN' | 'ES' : 'EN');
      }
      setShowMembers(hash === '#/members');
      setShowSaveTheDate(hash === '#/save-the-date');
      const isMember = hash.startsWith('#/member/');
      setShowMember(isMember);
      if (isMember) {
        setMemberSlug(hash.replace('#/member/', ''));
      }
      const isNews = hash.startsWith('#/news/');
      setShowNews(isNews);
      if (isNews) {
        const raw = hash.replace('#/news/', '');
        setNewsPostId(raw.split('?')[0]);
        const langMatch = raw.match(/[?&]lang=(EN|ES)/);
        setNewsLang(langMatch ? langMatch[1] as 'EN' | 'ES' : 'EN');
      }
      // When navigating back to root from a sub-page, skip the landing page
      if (hash === '' || hash === '#') {
        setShowLanding(false);
      }
    };
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, []);
  const handleReturnToLanding = () => {
    setShowLanding(true);
  };
  if (showAdmin) {
    return <Suspense fallback={<SectionLoader data-id="element-67" data-mp="1anhrrfpny9tc" />} data-id="element-66" data-mp="1ao1auzr1v48y">
        <AdminDashboard onBack={() => {
        window.location.hash = '';
        setShowAdmin(false);
      }} data-id="element-68" data-mp="1amyi0ma62rqe" />
      </Suspense>;
  }
  if (showConsular) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-69" data-mp="1kdktc4auow3a">
        <Suspense fallback={<SectionLoader data-id="element-71" data-mp="1ffrr1mco4m8a" />} data-id="element-70" data-mp="1fgaw42h7zg4w">
          <ConsularPage data-id="element-72" data-mp="1fhm125meitxl" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showBusinessMap) {
    return <LanguageProvider initialLanguage={businessMapLang} onLanguageSwitch={handleReturnToLanding} data-id="element-73" data-mp="mqlxtwvw9u6z">
        <Suspense fallback={<SectionLoader data-id="element-75" data-mp="11omke0avutog" />} data-id="element-74" data-mp="11o4rndnfixla">
          <BusinessMapPage data-id="element-76" data-mp="11ls32hn61oz9" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showMembers) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-77" data-mp="2d305wyi5yx1b">
        <Suspense fallback={<SectionLoader data-id="element-79" data-mp="2d54gu0ehcwb3" />} data-id="element-78" data-mp="2d1grzejzcy2p">
          <MembersDirectoryPage data-id="element-80" data-mp="284707s94cabp" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showSaveTheDate) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-81" data-mp="hnv3um3w3rcd">
        <Suspense fallback={<SectionLoader data-id="element-83" data-mp="ml4ologc3etv" />} data-id="element-82" data-mp="mlmttakz53dx">
          <SaveTheDatePage data-id="element-84" data-mp="mmg3okf8c90a" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showMember) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-85" data-mp="qm6qkd1my4zj">
        <Suspense fallback={<SectionLoader data-id="element-87" data-mp="vo5t5kpjwxvf" />} data-id="element-86" data-mp="vnnnxykwv9bd">
          <MemberProfilePage slug={memberSlug} data-id="element-88" data-mp="vk89mxz2a4b0" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showNews) {
    return <LanguageProvider initialLanguage={newsLang} data-id="element-89" data-mp="15k8skit0qieo">
        <Suspense fallback={<SectionLoader data-id="element-91" data-mp="1abh5hotk4lwl" />} data-id="element-90" data-mp="15inzyyq9l9jg">
          <NewsStoryPage postId={newsPostId} data-id="element-92" data-mp="1ab8r1shsd8ja" />
        </Suspense>
      </LanguageProvider>;
  }
  return <>
      <GoogleAnalytics data-id="element-93" data-mp="1ec146wts3xku" />
      <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-94" data-mp="1ecarudrcpsnd">
        <AppContent showLanding={showLanding} setShowLanding={setShowLanding} data-id="element-95" data-mp="1jd8assk6qqpx" />
      </LanguageProvider>
    </>;
}